import type {
	PrefetchContext,
	PrefetchedDataProps,
	PrefetchedUserDataProps,
	PrefetchUserContext,
} from 'src/shared/prefetch';

import { prefetchDs2ExperimentUserData, useAsyncDs2ExperimentData } from 'src/app/shared/hooks/ds2Experiment';
import type { Ds2ExperimentData } from 'src/app/shared/hooks/ds2Experiment';
import { useCountryCode, useLocale } from 'src/app/shared/hooks/l10n';
import type { MergedState } from 'src/app/shared/hooks/state';
import { useMergedStates } from 'src/app/shared/hooks/state';

import { prefetchDecisionMakerProfilePageDataFromApi, useDecisionMakerProfilePageDataFromApi } from './pageData';
import { prefetchDecisionMakerProfilePageFcm, useAsyncDecisionMakerProfilePageFcm } from './pageFcm';
import {
	prefetchDecisionMakerProfilePageFcmExperiments,
	useAsyncDecisionMakerProfilePageFcmExperiments,
} from './pageFcmExperiments';
import {
	prefetchDMProfilePagePaginatedResponsesDataFromApi,
	useDMProfilePagePaginatedResponsesDataFromApi,
} from './pageResponsesPaginatedData';
import {
	prefetchDMProfilePagePaginatedTargetedPetitionsDataFromApi,
	useDMProfilePagePaginatedTargetedPetitionsDataFromApi,
} from './pageTargetedPetitionsPaginatedData';

export type DecisionMakerProfilePagePrefetchedContext = Readonly<{
	pageData: Parameters<typeof useDecisionMakerProfilePageDataFromApi>[0];
	responsesPaginatedData: Parameters<typeof useDMProfilePagePaginatedResponsesDataFromApi>[0];
	targetedPetitionsPaginatedData: Parameters<typeof useDMProfilePagePaginatedTargetedPetitionsDataFromApi>[0];
}>;

export type DecisionMakerProfilePagePrefetchedUserContext = Readonly<{
	experiments: { ds2ExperimentData: Ds2ExperimentData };
	ds2Enabled: boolean;
}>;

export type DecisionMakerProfilePageCombinedState = MergedState<
	ReturnType<typeof useDecisionMakerProfilePageDataFromApi>,
	ReturnType<typeof useDMProfilePagePaginatedResponsesDataFromApi>,
	ReturnType<typeof useDMProfilePagePaginatedTargetedPetitionsDataFromApi>,
	ReturnType<typeof useAsyncDecisionMakerProfilePageFcmExperiments>,
	ReturnType<typeof useAsyncDecisionMakerProfilePageFcm>,
	ReturnType<typeof useAsyncDs2ExperimentData>
>;

export function usePageContext(
	slug: string,
	prefetchedData?: DecisionMakerProfilePagePrefetchedContext,
	prefetchedUserData?: DecisionMakerProfilePagePrefetchedUserContext,
): DecisionMakerProfilePageCombinedState {
	const l10n = {
		countryCode: useCountryCode(),
		locale: useLocale(),
	};

	return useMergedStates(
		useDecisionMakerProfilePageDataFromApi(prefetchedData?.pageData, slug),
		useDMProfilePagePaginatedResponsesDataFromApi(prefetchedData?.responsesPaginatedData, slug, l10n, undefined),
		useDMProfilePagePaginatedTargetedPetitionsDataFromApi(
			prefetchedData?.targetedPetitionsPaginatedData,
			slug,
			l10n,
			undefined,
		),
		useAsyncDecisionMakerProfilePageFcmExperiments(),
		useAsyncDecisionMakerProfilePageFcm(),
		useAsyncDs2ExperimentData(prefetchedUserData?.experiments.ds2ExperimentData),
	);
}

export async function prefetchPageContext(
	slug: string,
	context: PrefetchContext,
): Promise<DecisionMakerProfilePagePrefetchedContext> {
	const { ds2Enabled } = (await context.getUserData()) as unknown as DecisionMakerProfilePagePrefetchedUserContext;
	const { l10n } = context;

	const [pageData, responsesPaginatedData, targetedPetitionsPaginatedData] = await Promise.all([
		prefetchDecisionMakerProfilePageDataFromApi(context, slug),
		prefetchDMProfilePagePaginatedResponsesDataFromApi(context, slug, l10n, ds2Enabled),
		prefetchDMProfilePagePaginatedTargetedPetitionsDataFromApi(context, slug, l10n, ds2Enabled),
		prefetchDecisionMakerProfilePageFcmExperiments(context),
		prefetchDecisionMakerProfilePageFcm(context),
	]);

	return { pageData, responsesPaginatedData, targetedPetitionsPaginatedData };
}

export async function prefetchPageUserContext(
	context: PrefetchUserContext,
): Promise<DecisionMakerProfilePagePrefetchedUserContext> {
	const ds2ExperimentData = await prefetchDs2ExperimentUserData(context);

	return {
		experiments: { ds2ExperimentData },
		ds2Enabled: ds2ExperimentData.ds2FcmExperiment.fcmEnabled,
	};
}

export type DecisionMakerProfilePagePrefetchedDataProps =
	PrefetchedDataProps<DecisionMakerProfilePagePrefetchedContext> &
		PrefetchedUserDataProps<DecisionMakerProfilePagePrefetchedUserContext>;
